/**
 * Created by Jerker Nord on 2021-01-27.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BS2GameAssets} from "../BS2GameAssets";
import {gsap} from "gsap";
import {BS2} from "../BS2";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";
import {MathUtils} from "../utils/MathUtils";
import {BS2GameMode} from "../server/BS2GameMode";
import {BS2Sound} from "../config/sound/BS2Sound";
import {BS2Statics} from "../config/BS2Statics";
import {CustomWiggle} from "gsap/dist/CustomWiggle";

export class BS2BackgroundElements extends PIXI.Container implements StageLayer {
    private _mainView: PIXI.Container;
    private _logo: PIXI.Sprite;
    private _currentLevel: number = 0;
    private _bgSpine: PIXI.spine.Spine; //animations playing on multiple: tracks 0,1,2,3,4,5 for start loop and end, track 6 for BgWalk,track 7 for BgLevel, track 8 for cloud, track 9 for tree,track 10 for lightning during easter egg
    private _blackOverlay: PIXI.Sprite;
    private _demonSpine: PIXI.spine.Spine;

    private _defScaleValOfBgSpine: [number, number] = [0.2999, 0.2999]; //scaling value of x and y
    private _treeRepeatTl: gsap.core.Timeline | null;
    private _easterEggClickCount: number = 0;
    private _easterEggClickTarget: number = Math.floor(Math.random() * 3 + 3); // 3,4 or 5
    private _batSpine1: PIXI.spine.Spine;
    private _batSpine2: PIXI.spine.Spine;
    private _stageShakeTl: gsap.core.Timeline | undefined = undefined;

    constructor() {
        super();
        this.name = "BS2BackgroundElements";
        this.init();
        this.makeEasterEggClickable();
        SpineGSAPWrapper.createTimeline(this._bgSpine, 8, "Cloud_loop", -1);
        SpineGSAPWrapper.createTimeline(this._bgSpine, 9, "Tree_normal", 0);

    }

    public onResize() {
        if (NolimitApplication.isLandscape) {
            const extraSpaceWidth: number = Math.abs(NolimitApplication.screenBounds.left) - 30;
            const scale: number = Math.min(extraSpaceWidth / 200, 1);
            this._logo.scale.set(scale, scale);
            this._logo.x = -30 + (extraSpaceWidth * -0.5);
            this._logo.y = 130;

            if (scale < 0.48) {
                this._logo.visible = false;
            } else {
                this._logo.visible = true;
            }
            this._demonSpine.position.y = this.getDemonYPos(this._currentLevel, true);
        } else {
            const extraSpaceHeight: number = Math.abs(NolimitApplication.screenBounds.top);
            const scale: number = Math.min(extraSpaceHeight / 180, 1.2);
            this._logo.scale.set(scale, scale);
            this._logo.x = 360;
            this._logo.y = 25 + (extraSpaceHeight * -0.5);

            if (scale < 0.73) {
                this._logo.visible = false;
            } else {
                this._logo.visible = true;
            }
            this._demonSpine.position.y = this.getDemonYPos(this._currentLevel, false);
        }
        if (this._stageShakeTl && this._stageShakeTl.isActive()) {
            const leftDurr: number = this._stageShakeTl.totalDuration() - this._stageShakeTl.time();
            this._stageShakeTl.progress(1);
            this._stageShakeTl = undefined;
            this.stageShakeTl(leftDurr);
        }

        this._blackOverlay.width = NolimitApplication.screenBounds.width;
        this._blackOverlay.height = NolimitApplication.screenBounds.height;
    }

    public switchLevel(targetLevel: number, includeStart: boolean = true, forceStart: boolean = false, isFeatureBuySfs: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                this._demonSpine.position.y = this.getDemonYPos(targetLevel, NolimitApplication.isLandscape);
            }
        });
        if (this._currentLevel === targetLevel && !forceStart) {
            return tl;
        }
        targetLevel = MathUtils.constrain(targetLevel, 0, 5);

        const defaultMix: number = 0.1,
            animations = [],
            walkDurr: number = this._bgSpine.stateData.skeletonData.findAnimation("BGwalk").duration,
            walkDurr2Steps: number = this._bgSpine.stateData.skeletonData.findAnimation("BGwalk_2Steps").duration;

        let tempCurrentLevel: number = this._currentLevel;

        for (let i: number = 0; i <= 5; i++) {
            if (i == targetLevel) {
                const newLevelStart: gsap.core.Timeline = gsap.timeline();
                if (includeStart) {
                    newLevelStart.add([
                        BS2.reelView.switchLevel(targetLevel),
                        SpineGSAPWrapper.createTimeline(this._bgSpine, targetLevel, "start_level" + targetLevel, 0, 1, defaultMix),
                    ]);
                }
                newLevelStart.add(() => {
                    SpineGSAPWrapper.createTimeline(this._bgSpine, targetLevel, "loop_level" + targetLevel, -1, 1, defaultMix);
                });
                animations.push(newLevelStart);
            } else if (i < targetLevel) {
                const preLevelsLoop: gsap.core.Timeline = gsap.timeline();
                const trackEntry = this._bgSpine.state.getCurrent(i);
                if (trackEntry && trackEntry.animation?.name === "loop_level" + i) {
                    //nothing
                } else {
                    if (includeStart) {
                        preLevelsLoop.add([
                            SpineGSAPWrapper.createTimeline(this._bgSpine, i, "start_level" + i, 0, 1, defaultMix),
                            BS2.reelView.switchLevel(targetLevel),
                        ]);
                    }
                    preLevelsLoop.add(() => {
                        SpineGSAPWrapper.createTimeline(this._bgSpine, i, "loop_level" + i, -1);
                    });
                }
                animations.push(preLevelsLoop);
            } else if (i > targetLevel) {
                const levelReset: gsap.core.Timeline = gsap.timeline();
                if (this._bgSpine.state.getCurrent(i)?.animation?.name === "loop_level" + i) {
                    levelReset.add(SpineGSAPWrapper.createTimeline(this._bgSpine, i, "end_level" + i, 0));
                    levelReset.add(() => {
                        this._bgSpine.state.setEmptyAnimation(i, defaultMix)
                    });
                }
                animations.push(levelReset);
            }
        }

        //for bg
        this.startTreeRepeat(targetLevel);

        tl.add(animations);
        if (targetLevel < BS2.model.data.currentLevel) {
            BS2.soundPlayer.playSound(BS2Sound.LEVEL_RESET);
        }
        if (targetLevel < 5) {
            tl.add(gsap.to([BS2.reelView, BS2.winView], {duration: 1, y: BS2Statics.getShortScreenOffset()}), 0);
        }
        if (targetLevel === 0) {
            const blackOvelayTL: gsap.core.Timeline = gsap.timeline();
            const animDurr: number = 1;
            const easeEffect: string = "power1.out";
            tl.add([
                gsap.to(this._bgSpine.scale, {
                    x: this.getBgSpineScaleValue(targetLevel)[0],
                    y: this.getBgSpineScaleValue(targetLevel)[1],
                    duration: animDurr,
                    ease: easeEffect
                }),
                gsap.to(this._bgSpine, {
                    y: this.getBgSpineYPos(targetLevel),
                    duration: animDurr,
                    ease: easeEffect
                }),
                gsap.to(this._demonSpine, {
                    y: this.getDemonYPos(targetLevel, NolimitApplication.isLandscape),
                    duration: animDurr,
                    ease: easeEffect
                }), () => {
                    BS2.winView.onResize();
                    this._bgSpine.state.addAnimation(7, (BS2.model.data.mode === BS2GameMode.FREESPIN_SUPER || BS2.model.data.mode === BS2GameMode.AVALANCHE_FREESPIN_SUPER) ? "BG_Lvl5-0" : "BG_Lvl" + 0, false, 0)
                },
            ], 0);
            blackOvelayTL.to(this._blackOverlay, {
                duration: 0.5,
                alpha: 0.9,
                ease: easeEffect
            });
            blackOvelayTL.to(this._blackOverlay, {
                duration: 0.5,
                alpha: 0,
                ease: easeEffect
            });
            tl.add(blackOvelayTL, 0);
        } else {
            tl.add([
                gsap.to(this._bgSpine.scale, {
                    x: this.getBgSpineScaleValue(targetLevel)[0],
                    y: this.getBgSpineScaleValue(targetLevel)[1],
                    duration: this.getNumberOfSteps() === 2 ? walkDurr2Steps : walkDurr
                }),
                gsap.to(this._bgSpine, {
                    y: this.getBgSpineYPos(targetLevel),
                    duration: this.getNumberOfSteps() === 2 ? walkDurr2Steps : walkDurr
                }),
                gsap.to(this._demonSpine, {
                    y: this.getDemonYPos(targetLevel, NolimitApplication.isLandscape),
                    duration: this.getNumberOfSteps() === 2 ? walkDurr2Steps : walkDurr
                }),
                !forceStart ? SpineGSAPWrapper.createTimeline(this._bgSpine, 6, this.getNumberOfSteps() === 2 ? "BGwalk_2Steps" : "BGwalk", 0, 1, defaultMix) : gsap.timeline(),
            ], 0);
            tl.add(() => {
                if (!forceStart) {
                    this._demonSpine.state.setEmptyAnimation(0, 0)
                    this._demonSpine.state.clearTracks();
                    this._demonSpine.skeleton.setBonesToSetupPose();
                    this._demonSpine.skeleton.setSlotsToSetupPose();
                    this._demonSpine.state.addAnimation(0, this.getNumberOfSteps() == 2 ? "demonwalk_2Steps" : "demonwalk", false, 0);
                    for (let i: number = tempCurrentLevel; i <= targetLevel; i++) {
                        this._bgSpine.state.addAnimation(7, "BG_Lvl" + i, false, 0);
                    }

                    //#143 when return from higher level to lower level not equal to zero. eg from sfs to play xBet1/xBet2
                    if (targetLevel < tempCurrentLevel) {
                        this._bgSpine.state.addAnimation(7, "BG_Lvl" + targetLevel, false, 0);
                        if (targetLevel !== 5) {
                            gsap.to(BS2.reelView, {
                                duration: this.getNumberOfSteps() === 2 ? walkDurr2Steps : walkDurr,
                                y: 19
                            })
                            gsap.to(this._demonSpine, {
                                y: this.getDemonYPos(targetLevel, NolimitApplication.isLandscape),
                                duration: this.getNumberOfSteps() === 2 ? walkDurr2Steps : walkDurr
                            })
                        }
                    }
                } else {
                    SpineGSAPWrapper.createTimeline(this._bgSpine, 7, "BG_Lvl" + targetLevel).progress(1);
                }
            }, 0);
            if (targetLevel === 5) {
                tl.to([BS2.reelView, BS2.winView], {
                    duration: this.getNumberOfSteps() === 2 ? walkDurr2Steps : walkDurr,
                    y: 34
                }, 0);
                tl.add(() => {
                    BS2.winView.onResize();
                })
            }
        }
        this._currentLevel = targetLevel;
        return tl;
    }


    private getDemonYPos(targetValue: number, isLandscape: boolean): number {
        let yPos: number = isLandscape ? 850 : 1050;
        switch (targetValue) {
            case 1:
                yPos = isLandscape ? 850 : 1000;
                break;
            case 2:
                yPos = isLandscape ? 850 : 980;
                break;
            case 3:
                yPos = isLandscape ? 850 : 970;
                break;
            case 4:
                yPos = isLandscape ? 850 : 970;
                break;
            case 5:
                yPos = isLandscape ? 850 : 970;
                break;
        }
        return yPos;
    }

    private getBgSpineYPos(target: number): number {
        switch (target) {
            case 0:
                return 360;
            case 1:
                return 360;
            case 2:
                return 360;
            case 3:
                return 385;
            case 4:
                return 450;
            case 5:
                return 500;
        }
        return 360;
    }

    private getBgSpineScaleValue(target: number): number[] {
        return [this._defScaleValOfBgSpine[0] + (((1 - this._defScaleValOfBgSpine[0]) / 5) * target), this._defScaleValOfBgSpine[1] + (((1 - this._defScaleValOfBgSpine[1]) / 5) * target)]
    }

    private getNumberOfSteps(): number {
        const currLevel: number = BS2.model.currentLevel,
            nextLevel: number = BS2.model.nextLevel;
        return ((nextLevel - currLevel === 1) || (currLevel === 3 && nextLevel === 5)) ? 1 : 2;
    }


    private init() {
        this._mainView = new PIXI.Container();
        this._mainView.name = "mainCont";
        this._logo = this.createLogoSprite();
        this._bgSpine = this.createBgSpine();
        this._batSpine1 = this.createBatSpine();
        this._batSpine2 = this.createBatSpine();
        this._blackOverlay = this.createBlackOverlaySprite();
        this._demonSpine = this.createDemonSpine();
        this._mainView.addChild(this._demonSpine, this._batSpine2, this._batSpine1, this._logo, this._blackOverlay);
        this.addChild(this._bgSpine, this._mainView);
        this.onResize();
    }

    private createBgSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(BS2.assetLoader.getSpineAsset(BS2GameAssets.BACKGROUND_SPINE));
        spine.name = "bgSpine";
        spine.position.set(360, 360);
        spine.scale.set(this._defScaleValOfBgSpine[0], this._defScaleValOfBgSpine[1]);
        return spine;
    }

    private createBatSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(BS2.assetLoader.getSpineAsset(BS2GameAssets.BAT_SPINE));
        spine.name = "batSpine";
        spine.position.set(360, 560);
        return spine;
    }

    private createRedOverlaySprite(): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(BS2.assetLoader.getTextures("bg/redOverlay")[0]);
        sprite.name = "redOverlaySprite";
        sprite.position.set(355, 360);
        sprite.anchor.set(0.5, 0.5);
        return sprite;
    }

    private createDemonSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(BS2.assetLoader.getSpineAsset("spine/demon.json"));
        spine.name = "demonSpine";
        spine.position.set(390, 720);
        // spine.scale.set(0.4, 0.4);
        return spine;
    }

    private createBlackOverlaySprite(): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(PIXI.Texture.WHITE);
        sprite.name = "blackOverlaySprite";
        sprite.tint = PIXI.utils.string2hex("#000000");
        sprite.position.set(360, 360);
        sprite.anchor.set(0.5, 0.5);
        sprite.alpha = 0;
        return sprite;
    }

    private createLogoSprite(): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(BS2.assetLoader.getTextures("Logo")[0]);
        sprite.name = "Logo";
        sprite.anchor.set(0.5, 0.5);
        sprite.interactive = true;
        sprite.buttonMode = true;
        return sprite;

    }

    private startTreeRepeat(targetLevel: number): void {
        if (this._treeRepeatTl) {
            this._treeRepeatTl.kill();
            this._treeRepeatTl = null;
        }
        if (targetLevel === 0) {
            this._treeRepeatTl = gsap.timeline();
            this._treeRepeatTl.add(SpineGSAPWrapper.createTimeline(this._bgSpine, 9, "Tree_normal", 0));
        } else {
            this._treeRepeatTl = gsap.timeline({
                repeat: -1,
                repeatDelay: (5 - targetLevel) * 5
            });
            this._treeRepeatTl.add(SpineGSAPWrapper.createTimeline(this._bgSpine, 9, "Tree_loop", 0));
        }
    }

    private makeEasterEggClickable() {
        this._logo.on("mousedown", () => {
            this.handleEasterEgg();
        })
        this._logo.on("tap", () => {
            this.handleEasterEgg();
        })
    }

    private handleEasterEgg() {
        const tl: gsap.core.Timeline = gsap.timeline();
        this._easterEggClickCount++;
        if (this._easterEggClickCount === this._easterEggClickTarget) {
            this._easterEggClickCount = 0;
            this._easterEggClickTarget = Math.floor(Math.random() * 3 + 3);
            tl.add(() => {
                BS2.soundPlayer.playSound(BS2Sound.EASTER_EGG);
                this._logo.interactive = false;
                resetSpine(this._batSpine1);
                resetSpine(this._batSpine2);
            });

            tl.add(SpineGSAPWrapper.createTimeline(this._bgSpine, 10, "lightning", 1));
            tl.add([SpineGSAPWrapper.createTimelineForAnimation(this._batSpine1, 0, "bat_Fly", false), () => {
                this.stageShakeTl(1);
            }], "<+=0.5");
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(this._batSpine2, 0, "bat_Fly", false), "<+=0.5");

            tl.add(() => {
                this._logo.interactive = true;
            });
        }

        function resetSpine(spine: PIXI.spine.Spine): void {
            spine.state.setEmptyAnimation(0, 0);
            spine.state.setEmptyAnimation(1, 0)
            spine.state.clearTracks();
            spine.skeleton.setBonesToSetupPose();
            spine.skeleton.setSlotsToSetupPose();
        }

    }

    private stageShakeTl(duration: number): void {
        const isSsfsPosition: boolean = BS2.model.data.mode === BS2GameMode.FREESPIN_SUPER || BS2.model.data.nextMode === BS2GameMode.FREESPIN_SUPER || BS2.model.data.mode === BS2GameMode.AVALANCHE_FREESPIN_SUPER || BS2.model.data.nextMode === BS2GameMode.AVALANCHE_FREESPIN_SUPER;
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                this._stageShakeTl = undefined;
            }
        });
        BS2.reelView.position.set(0, isSsfsPosition ? 34 : BS2Statics.getShortScreenOffset());
        tl.add(gsap.to([BS2.reelView, BS2.backgroundElements], {
            duration: duration,
            x: "-=15",
            y: "-=15",
            ease: CustomWiggle.create("easterEgg", {
                wiggles: 60,
                amplitudeEase: "none",
                timingEase: "none",
            })
        }));
        this._stageShakeTl = gsap.timeline();
        this._stageShakeTl?.add(tl);
    }


}